import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { bookingFormActions } from './booking-form.reducer';
import { BookingFormData } from './booking-form.state';
import { selectBookingFormData } from '../reducers';
import { BookingsService } from '../../services/bookings.service';
import { DatetimeHelper } from '../../helpers/datetime.helper';
import { parseISO } from 'date-fns';
import { BookingType } from '../../enums/booking-type';
import { GetBookingCostsRequest } from '../../requests/get-booking-costs.request';
import { buildBookingDateBlocks } from '../../helpers/booking-dates.helper';
import { ToastService } from '../../services/toast.service';

@Injectable()
export class BookingFormEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private bookingsService = inject(BookingsService);
  private toastService = inject(ToastService);

  findBookingCosts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingFormActions.findBookingCosts),
      withLatestFrom(this.store.select(selectBookingFormData)),
      switchMap(([_, booking]) => {
        if (!this.hasRequiredBookingData(booking)) {
          return of(bookingFormActions.findBookingCostsFailure({}));
        }

        const payload = this.buildCostsPayload(booking, booking.coupon_code ?? null);

        return this.bookingsService.getCosts(payload).then(
          (invoice) => bookingFormActions.findBookingCostsSuccess({ invoice }),
          (error) => {
            console.error('Error finding booking costs:', error);
            return bookingFormActions.findBookingCostsFailure({
              error: error?.error?.message || error?.message || 'Erreur lors du calcul des coûts',
            });
          },
        );
      }),
    ),
  );

  validateCoupon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingFormActions.validateCoupon),
      withLatestFrom(this.store.select(selectBookingFormData)),
      switchMap(([action, booking]) => {
        if (!this.hasRequiredBookingData(booking)) {
          this.toastService.error('Veuillez d\'abord sélectionner une salle et une date');
          return of(bookingFormActions.validateCouponFailure({ error: 'Missing required data' }));
        }

        const payload = this.buildCostsPayload(booking, action.couponCode);

        return this.bookingsService.getCosts(payload).then(
          (invoice) => {
            this.toastService.success('Code de réduction appliqué');
            return bookingFormActions.validateCouponSuccess({
              couponCode: action.couponCode,
              invoice,
            });
          },
          (error) => {
            console.error('Error validating coupon:', error);
            this.toastService.error('Code de réduction invalide');
            return bookingFormActions.validateCouponFailure({
              error: error?.error?.message || error?.message || 'Invalid coupon',
            });
          },
        );
      }),
    ),
  );

  private hasRequiredBookingData(booking: BookingFormData): boolean {
    const hasDates =
      booking.booking_type === BookingType.MultipleDates
        ? (booking.multiple_dates?.length ?? 0) > 0
        : !!booking.start;

    return hasDates && !!booking.room_id;
  }

  private buildCostsPayload(
    booking: BookingFormData,
    couponCode: string | null,
  ): GetBookingCostsRequest {
    return {
      room_id: booking.room_id!,
      complementary_rooms_ids: booking.complementary_rooms_ids ?? [],
      booking_type: booking.booking_type,
      room_amenities_ids: booking.room_amenities_ids ?? [],
      booking_options_ids: booking.booking_options_ids ?? [],
      amenities_extras_ids: booking.amenities_extras_ids ?? [],
      rate_id: booking.rate_id!,
      coupon_code: couponCode,
      booking_id: booking.id ?? null,
      dates: buildBookingDateBlocks(booking),
      recurrence: this.buildRecurrence(booking),
    };
  }

  private buildRecurrence(booking: BookingFormData): GetBookingCostsRequest['recurrence'] {
    if (!booking.recurrence) {
      return undefined;
    }

    return {
      ...booking.recurrence,
      end: DatetimeHelper.dateToApiFormat(parseISO(booking.recurrence.end), true),
    };
  }
}
